import React, { useState } from 'react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Input, Select } from '../ui/Field';
import { FeatureGate, isLive } from '../platform/FeatureStatusBadge';
import { billCategories } from '../../data/finance';
import { naira } from '../../utils/format';
const FLOW = ['Reserve from available balance', 'Send to bill aggregator', 'Await biller confirmation', 'Finalise on success, or reverse on failure'];
export function BillsPanel() {
  const [categoryId, setCategoryId] = useState(billCategories[0].id);
  const category = billCategories.find(item => item.id === categoryId) ?? billCategories[0];
  const [providerId, setProviderId] = useState(category.providers[0].id);
  const provider = category.providers.find(item => item.id === providerId) ?? category.providers[0];
  const [productId, setProductId] = useState(provider.products[0].id);
  const product = provider.products.find(item => item.id === productId) ?? provider.products[0];
  const [amount, setAmount] = useState('2000');
  function selectCategory(id: string) {
    const next = billCategories.find(item => item.id === id) ?? billCategories[0];
    setCategoryId(id);
    setProviderId(next.providers[0].id);
    setProductId(next.providers[0].products[0].id);
  }
  function selectProvider(id: string) {
    const next = category.providers.find(item => item.id === id) ?? category.providers[0];
    setProviderId(id);
    setProductId(next.products[0].id);
  }
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-3152" data-mp="25lz1brwdd8bm">
      <Panel title="Bills and recharge" description="Category → provider → product → customer identifier → transaction. No biller is hard-coded into the interface." data-id="element-3153" data-mp="20l1kpjr0cmc8">
        <div className="grid gap-4 sm:grid-cols-2" data-id="element-3154" data-mp="p77jg8wunjgk">
          <Field label="Category" htmlFor="bill-category" data-id="element-3155" data-mp="p4utb8gxu2sf">
            <Select id="bill-category" value={categoryId} onChange={event => selectCategory(event.target.value)} data-id="element-3156" data-mp="k6t8ahzybteb">
              {billCategories.map(item => <option key={item.id} value={item.id} data-id="element-3157" data-mp="qnl42geqtoaj">
                  {item.name}
                </option>)}
            </Select>
          </Field>
          <Field label="Provider" htmlFor="bill-provider" data-id="element-3158" data-mp="hmn7ukajql2i">
            <Select id="bill-provider" value={providerId} onChange={event => selectProvider(event.target.value)} data-id="element-3159" data-mp="hp9dgedvmqi3">
              {category.providers.map(item => <option key={item.id} value={item.id} data-id="element-3160" data-mp="3q3hwovuie4xl">
                  {item.name}
                </option>)}
            </Select>
          </Field>
          <Field label="Product" htmlFor="bill-product" data-id="element-3161" data-mp="3u2qtk9kuiixd">
            <Select id="bill-product" value={productId} onChange={event => setProductId(event.target.value)} data-id="element-3162" data-mp="3g63jr67snya1">
              {provider.products.map(item => <option key={item.id} value={item.id} data-id="element-3163" data-mp="31ivesn52wp6i">
                  {item.name}
                  {item.priceNaira ? ` — ${naira(item.priceNaira)}` : ''}
                </option>)}
            </Select>
          </Field>
          <Field label={product.identifierLabel} htmlFor="bill-identifier" data-id="element-3164" data-mp="3be6mili2kw4r">
            <Input id="bill-identifier" placeholder={product.identifierLabel} data-id="element-3165" data-mp="36g513tx68kq4" />
          </Field>
          {product.priceNaira === null && <Field label="Amount" htmlFor="bill-amount" hint="Open-value product" data-id="element-3166" data-mp="3fio2e9lb6pa9">
              <Input id="bill-amount" value={amount} onChange={event => setAmount(event.target.value)} inputMode="numeric" data-id="element-3167" data-mp="3u70yd6kec84r" />
            </Field>}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4" data-id="element-3168" data-mp="734dfmro7t10">
          <p className="cz-num text-[13px] text-ink-muted" data-id="element-3169" data-mp="32aqowpysk9p">
            Total{' '}
            <span className="font-semibold text-ink" data-id="element-3170" data-mp="1winf5h287p4h">
              {naira(product.priceNaira ?? (Number(amount) || 0))}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-3" data-id="element-3171" data-mp="2699myp3pbbcm">
            <Button variant="primary" disabled={!isLive('bills')} data-id="element-3172" data-mp="269rqm6sp0ruk">
              Pay
            </Button>
            <FeatureGate featureKey="bills" inline data-id="element-3173" data-mp="2b7jjb315whzt" />
          </div>
        </div>
        <div className="mt-4" data-id="element-3174" data-mp="1dlvx1ayf7g20">
          <FeatureGate featureKey="bills" data-id="element-3175" data-mp="1dmxblpenakoh" />
        </div>
        <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-3176" data-mp="1ikqjcmvljmqv">
          Bill payment is performed by a licensed aggregator. Campuzon records the transaction and its
          state; it does not settle with billers on its own account.
        </p>
      </Panel>

      <div className="space-y-6" data-id="element-3177" data-mp="2yy75mtbtra5w">
        <Panel title="Transaction flow" bodyClassName="p-0" data-id="element-3178" data-mp="2ywvug8g1v04r">
          <ol className="divide-y divide-line" data-id="element-3179" data-mp="32z8v4ifxw7uo">
            {FLOW.map((step, index) => <li key={step} className="flex gap-3 px-5 py-3" data-id="element-3180" data-mp="rc5ns2sxgzi6" data-mpi={index}>
                <span className="cz-num flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-line text-[11px] font-semibold text-ink-muted" data-id="element-3181" data-mp="rbdsyu75n5sy">
                  {index + 1}
                </span>
                <span className="text-[13px] leading-5 text-ink-muted" data-id="element-3182" data-mp="ihr31kpmowro">{step}</span>
              </li>)}
          </ol>
          <div className="border-t border-line px-5 py-3" data-id="element-3183" data-mp="8m76cw38pu1v">
            <p className="text-[12px] leading-5 text-ink-muted" data-id="element-3184" data-mp="8jkz6xk9hgk6">
              If the biller declines, the reserved amount is released by a reversal transaction — the
              failed record stays in your ledger beside it.
            </p>
          </div>
        </Panel>
        <Panel title="Configuration" data-id="element-3185" data-mp="3qslt9kjhlece">
          <p className="text-[13px] leading-6 text-ink-muted" data-id="element-3186" data-mp="3qtn8803mikze">
            Categories, providers and products are administered from the Campuzon admin console.
            Adding a new biller is configuration, not a release.
          </p>
          <Badge tone="warn" className="mt-3" data-id="element-3187" data-mp="284aj9a19hkg6">
            Gated — awaiting licensed aggregator
          </Badge>
        </Panel>
      </div>
    </div>;
}